/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Typography} from '@mui/material';
import React, {useEffect, useState} from 'react';
import ArgosFlexbox from '../../../../../../components/Containers/ArgosFlexbox';
import {ArgosWarningIcon} from '../../../../../../components/Icons/ArgosActionIcons';
import Loader from "../../../../../../components/Loader/Loader";
import {MERGED_DATASET_TYPE_STR, SINGLE_DATASET_TYPE_STR} from "../../../constants";
import {AnalysisResultsLeftPanel} from './AnalysisResultTab/AnalysisResultsLeftPanel';
import {AnalysisResultsRightPanel} from './AnalysisResultTab/AnalysisResultsRightPanel';
import {LOADER_WRAPPER_STYLE} from "./constants";
import { List } from 'lodash';
import {PropertiesData, ObjectPropertiesData} from "./RightPanelCoverageRequirements";

interface AnalysisResultsProps {
  properties: PropertiesData[] | ObjectPropertiesData[] // List of properties for the dataset, including objects and their attributes
  resultPlottingData: any // Data used for plotting results
  datasetType: string // ex: "Single"
  isLoadingRawData: boolean
  errorFetchingRawData: boolean
  loadRawDatasetsData: any // Unique ID representing raw dataset load
  preselectedObjects: any // Full list of objects to plot from
  fetchData: (fetchRaw: boolean, showMainLoader: boolean, doeValues?: List<String>, 
    x_axis?: string, y_axis?: string, objectIds?: List<String>) => void
  onPlotCheckboxesChange?: (plottingList: any) => void // Callback function to handle changes in the plotting checkboxes
  isLoading?: boolean
  outOfDate?: boolean
  allowUsingDoeVarsAsAxes?: boolean
  isNewReportGraph?: boolean // true if is Analysis Graph inside Create New Report window
}

/**
 * AnalysisResults component includes a graph representing data for either a single or merged dataset,
 * and and checklist tree on the left hand side to select objects to plot.
 */
const AnalysisResults = React.memo((props: AnalysisResultsProps) => {
  const [navigationList, setNavigationList] = useState<Record<string, ObjectPropertiesData[]>>({}) // Object to hold dataset properties grouped by category for navigation (map of category to list of object properties)
  const [plottingList, setPlottingList] = React.useState<any>([])
  const [selectedCategories, setSelectedCategories] = React.useState<string[]>([])
  const outOfDate = props.outOfDate

  /**
   * Effect to prepare the data for plotting (create navigation list and reset selected categories) when properties or resultPlottingData change.
   */
  useEffect(() => {
    setSelectedCategories([])
    prepareDataForPlotting()
  }, [props.properties, props.resultPlottingData])

  /**
   * Prepares a navigation object grouping dataset properties by their category.
   * Only includes properties of type "Object" that exist in the resultPlottingData.
   * The result is set in the navigationList state for use in the left panel navigation.
   */
  const prepareDataForPlotting = () => {
    // Filter properties to only include those of type "Object"
    let objects = props?.properties?.filter((property: any) => property.type == "Object")
    let localNavigationObject: any = {}

    // Iterate over each object to group them by category
    objects.forEach((objectElement: any) => {
      const resultPlottingData = props?.resultPlottingData
      if (!resultPlottingData) return // Skip if plotting data is not available

      // Only include objects that exist in the resultPlottingData
      if (Object.keys(props.resultPlottingData)?.indexOf(objectElement.objectId) != -1) {
        // If the category is not yet in the navigation object, initialize it as an array
        if (Object.keys(localNavigationObject).indexOf(objectElement.category) == -1) {
          localNavigationObject[objectElement.category] = [objectElement]
        } else {
          // Otherwise, push the object into the existing category array
          localNavigationObject[objectElement.category].push(objectElement)
        }
      }
    })

    // Update the navigationList state with the grouped objects
    setNavigationList(localNavigationObject)
  }

  useEffect(() => {
    if (props.onPlotCheckboxesChange) {
      props.onPlotCheckboxesChange(plottingList)
    }
  }, [plottingList]);

  useEffect(() => {
    if (props.datasetType == SINGLE_DATASET_TYPE_STR) {
      if (props?.preselectedObjects == null) return
      let _plottingList: any = []
      props?.preselectedObjects?.forEach((objectId: string) => {
        if (objectId) {
          _plottingList.push(objectId)
        }
      })
      setPlottingList(_plottingList)
    }
  }, [props?.preselectedObjects]);

  return (
    <>
      <Loader isLoading={props?.isLoading ?? false} wrapperStyle={LOADER_WRAPPER_STYLE}>
        <ArgosFlexbox vertical style={{height: "100%", paddingBottom: "25px", overflowX: 'auto'}}>
          {
            outOfDate && <ArgosFlexbox style={{padding: "15px", width: "100%"}}>
              <ArgosFlexbox style={{padding: "15px", width: "100%", backgroundColor: "#FDF0E2"}}>
                <ArgosWarningIcon/>
                <Typography align={'center'} style={{color: "#A43D15"}}>
                  The results shown are out-of-date and do not reflect the recent changes made to one or more source datasets.
                </Typography>
              </ArgosFlexbox>
            </ArgosFlexbox>
          }
          <ArgosFlexbox>
            <AnalysisResultsLeftPanel
              data={navigationList}
              datasetType={props.datasetType}
              plottingList={plottingList}
              setPlottingList={props.datasetType == SINGLE_DATASET_TYPE_STR ? () => {
              } : setPlottingList}
              selectedCategories={selectedCategories}
              setSelectedCategories={setSelectedCategories}
              preselectedObjects={props.preselectedObjects}
              isNewReportGraph={props.isNewReportGraph}
            />

            <AnalysisResultsRightPanel
              datasetType={props.datasetType}
              allowUsingDoeVarsAsAxes={props.allowUsingDoeVarsAsAxes}
              isMerged={props.datasetType == MERGED_DATASET_TYPE_STR}
              objectToLineTypeMap={props?.resultPlottingData}
              objectsToPlot={plottingList}
              fetchData={props.fetchData}
              isLoadingRawData={props.isLoadingRawData}
              errorFetchingRawData={props.errorFetchingRawData}
              loadRawDatasetsData={props.loadRawDatasetsData}
              isNewReportGraph={props.isNewReportGraph}
            />
          </ArgosFlexbox>
        </ArgosFlexbox>
      </Loader>
    </>
  )
})
export {AnalysisResults};
